import i18n from '../i18n';

const COMPANY_NAME = `${process.env.REACT_APP_COMPANY_NAME}, ${process.env.REACT_APP_COMPANY_CITY}`;

/**
 * Global helper to print table data from pages.
 * @param {Array} data - The array of records to print.
 * @param {Array} columns - The column definitions used by the DataTable.
 * @param {String} title - The title of the print document.
 */
export const printTableData = (data, columns, title = 'Report') => {
  if (!data || data.length === 0) {
    alert('No data available to print.');
    return;
  }

  // 1. Filter out columns to exclude
  const excludedIds = ['actions', 'status', 'status_actions', 'Update Status', 'status_actions_column'];
  let filteredColumns = columns.filter(col =>
    !excludedIds.includes(col.id) &&
    !excludedIds.includes(col.label) &&
    col.label !== 'Actions'
  );

  // 2. Custom Column Logic for Purchase and Sales Daybooks
  const lowTitle = title.toLowerCase();
  const isPurchaseDaybook = lowTitle.includes('purchase daybook');
  const isSalesDaybook = lowTitle.includes('sales daybook');

  if (isPurchaseDaybook) {
    filteredColumns = [
      { id: 'date', label: 'Date' },
      { id: 'serial_number', label: 'Serial Number' },
      { id: 'po_number', label: 'PO Number' },
      { id: 'tally_name', label: 'Tally Name' },
      { id: 'vehicle_number', label: 'Vehicle Number', getValue: (item) => item.vehicle_number || item.vehicle_numbers || i18n.t('common.symbols.emDash') },
      { id: 'actual_quantity', label: 'Total Weight' },
      { id: 'total_amount', label: 'Total Amount' },
      { id: 'remark', label: 'Remarks', highlight: true },
    ];
  } else if (isSalesDaybook) {
    filteredColumns = [
      { id: 'date', label: 'Date' },
      { id: 'invoice_number', label: 'Invoice Number' },
      { id: 'order_number', label: 'Order Number' },
      { id: 'tally_name', label: 'Tally Name' },
      { id: 'vehicle_number', label: 'Vehicle Number' },
      { id: 'actual_quantity', label: 'Total Quantity' },
      { id: 'total_amount', label: 'Total Amount' },
      { id: 'remark', label: 'Remarks', highlight: true },
    ];
  }

  // 3. Insert "Challan Number" logic (skipped for Daybooks as they have custom columns)
  if (!isPurchaseDaybook && !isSalesDaybook) {
    const firstItem = data[0] || {};
    const hasChallanFields = 'challan_number' in firstItem || 'sub_order_number' in firstItem || 'advice_number' in firstItem;
    if (hasChallanFields) {
      const challanColumn = {
        id: 'custom_challan_number',
        label: 'Challan Number',
        getValue: (item) => item.challan_number || item.sub_order_number || item.order_number || i18n.t('common.symbols.emDash'),
      };
      filteredColumns.splice(2, 0, challanColumn);
    }
  }

  // 4. Date / time for footer
  const now = new Date().toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });

  const getCellValue = (item, col) => {
    if (col.getValue) return col.getValue(item);

    const value = item[col.id];

    if (col.id === 'date' || col.id === 'advice_date' || col.id === 'created_at') {
      if (!value) return i18n.t('common.symbols.emDash');
      return new Date(value).toLocaleDateString('en-GB');
    }

    if (col.id === 'total_amount' || col.id === 'amount' || col.id === 'subtotal') {
      return value != null ? `&#8377;${Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '&mdash;';
    }

    if (col.id === 'total_quantity' || col.id === 'quantity' || col.id === 'actual_quantity') {
      const qty = item.actual_quantity || item.total_delivered_quantity || item.total_quantity || item.quantity || 0;
      const unit = item.unit_type || item.unit || '';
      return `${qty} ${unit}`.trim();
    }

    return value != null ? String(value) : i18n.t('common.symbols.emDash');
  };

  const tableHeaders = filteredColumns.map(col => `<th>${col.label}</th>`).join('');

  const companyDetailsHtml = [
    process.env.REACT_APP_COMPANY_ADDRESS ? `<div class="company-sub">${process.env.REACT_APP_COMPANY_ADDRESS}</div>` : '',
    (process.env.REACT_APP_COMPANY_GST || process.env.REACT_APP_COMPANY_PAN) ? `<div class="company-sub">${[process.env.REACT_APP_COMPANY_GST ? 'GST: ' + process.env.REACT_APP_COMPANY_GST : '', process.env.REACT_APP_COMPANY_PAN ? 'PAN: ' + process.env.REACT_APP_COMPANY_PAN : ''].filter(Boolean).join(' | ')}</div>` : '',
    process.env.REACT_APP_COMPANY_BANK_DETAILS ? `<div class="company-sub">${process.env.REACT_APP_COMPANY_BANK_DETAILS}</div>` : '',
  ].filter(Boolean).join('');

  const tableRows = data.map((item, idx) => {
    const cells = filteredColumns.map(col => {
      const value = getCellValue(item, col);
      const highlightClass = col.highlight ? ' class="highlight-cell"' : '';
      return `<td${highlightClass}>${value}</td>`;
    }).join('');
    return `<tr class="${idx % 2 === 1 ? 'alt' : ''}">${cells}</tr>`;
  }).join('');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title} — ${COMPANY_NAME}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      font-size: 13px;
      color: #1a1a2e;
      background: #fff;
      padding: 28px 32px;
    }

    /* ── Company letterhead ── */
    .letterhead {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 14px;
      border-bottom: 3px solid #05445E;
      margin-bottom: 6px;
    }
    .company-name {
      font-size: 20px;
      font-weight: 800;
      color: #05445E;
      letter-spacing: -0.3px;
    }
    .company-sub {
      font-size: 10px;
      color: #6b7280;
      margin-top: 3px;
    }
    .print-btn-wrap {
      /* hidden on actual print */
    }
    .print-btn {
      padding: 8px 20px;
      background: #189AB4;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      letter-spacing: 0.2px;
    }
    .print-btn:hover { background: #05445E; }

    /* ── Report title bar ── */
    .report-meta {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      margin: 14px 0 16px;
    }
    .report-title {
      font-size: 17px;
      font-weight: 700;
      color: #111827;
    }
    .report-info {
      font-size: 10px;
      color: #6b7280;
      text-align: right;
      line-height: 1.6;
    }
    .badge {
      display: inline-block;
      background: #e0f2fe;
      color: #0369a1;
      border-radius: 20px;
      padding: 2px 10px;
      font-size: 11px;
      font-weight: 700;
      margin-top: 4px;
    }

    /* ── Table ── */
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
    }
    thead tr {
      background: #05445E;
    }
    thead th {
      color: #fff;
      text-align: left;
      padding: 9px 10px;
      font-weight: 700;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      white-space: nowrap;
      border-right: 1px solid rgba(255,255,255,0.12);
    }
    thead th:last-child { border-right: none; }
    tbody td {
      padding: 7px 10px;
      border-bottom: 1px solid #e5e7eb;
      border-right: 1px solid #f3f4f6;
      vertical-align: top;
      color: #1f2937;
      font-weight: 500;
    }
    tbody td:last-child { border-right: none; }
    tbody tr.alt td { background: #f8fafc; }
    tbody tr:hover td { background: #f0f9ff; }
    tbody tr:last-child td { border-bottom: 2px solid #05445E; }

    .highlight-cell {
      background: #fef9c3 !important;
      font-weight: 600;
      color: #854d0e;
    }

    /* ── Footer ── */
    .footer {
      margin-top: 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 9.5px;
      color: #9ca3af;
      border-top: 1px solid #e5e7eb;
      padding-top: 10px;
    }

    /* ── Print overrides ── */
    @media print {
      body { padding: 16px 20px; }
      .print-btn-wrap { display: none !important; }
      table { page-break-inside: auto; font-size: 11px; }
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; page-break-after: auto; }
      .footer { position: fixed; bottom: 0; left: 20px; right: 20px; background: #fff; }
    }
  </style>
</head>
<body>

  <!-- Letterhead -->
  <div class="letterhead">
    <div>
      <div class="company-name">${COMPANY_NAME}</div>
      <div class="company-sub">Enterprise Resource Planning &nbsp;|&nbsp; Internal Document</div>
      ${companyDetailsHtml}
    </div>
    <div class="print-btn-wrap">
      <button class="print-btn" onclick="window.print()">&#128438;&nbsp; Print</button>
    </div>
  </div>

  <!-- Report meta -->
  <div class="report-meta">
    <div>
      <div class="report-title">${title}</div>
      <span class="badge">${data.length} record${data.length !== 1 ? 's' : ''}</span>
    </div>
    <div class="report-info">
      <div>Printed on: ${now}</div>
    </div>
  </div>

  <!-- Data table -->
  <table>
    <thead>
      <tr>${tableHeaders}</tr>
    </thead>
    <tbody>
      ${tableRows}
    </tbody>
  </table>

  <!-- Footer -->
  <div class="footer">
    <span>${COMPANY_NAME}</span>
    <span>Generated for internal use only &nbsp;&bull;&nbsp; &copy; ${new Date().getFullYear()}</span>
  </div>

</body>
</html>`;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.documentElement.innerHTML = html;
  } else {
    alert('Please allow popups to print the report.');
  }
};
